/**
 * Owns the recovery-step form itself — the form state, the per-step-type body,
 * and submission — so that RecoveryStepForm stays a pure router (title, the
 * type-selection screen, and which body to render).
 */
import classNames from 'classnames/bind';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, Input } from 'shared/io';
import type { RecoverActionType, RecoveryPlanStep } from 'types/recovery-plan';
import styleIdentifiers from './RecoveryStepForm.module.scss';
import { RecoveryStepFormAICall } from './RecoveryStepFormAICall';
import { RecoveryStepFormCreateTask } from './RecoveryStepFormCreateTask';
import { RecoveryStepFormSendReminder } from './RecoveryStepFormSendReminder';

const styles = classNames.bind(styleIdentifiers);

export type RecoveryStepFormType = 'task_generation' | 'send_reminder' | 'initiate_ai_call';

type Props = {
  type: RecoveryStepFormType;
  title: string;
  step?: RecoveryPlanStep;
  availableTemplateTypes: RecoverActionType[];
  onSelectTemplate: (step: RecoveryPlanStep) => void;
};

export const RecoveryStepFormBody = ({
  type,
  title,
  step,
  availableTemplateTypes,
  onSelectTemplate,
}: Props) => {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();

  const form = useForm<RecoveryPlanStep>({
    shouldUnregister: true,
    defaultValues: step,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const submit = (values: RecoveryPlanStep) => {
    onSelectTemplate({
      ...step,
      ...values,
    });
  };

  const isNotTaskOrAICall = type !== 'task_generation' && type !== 'initiate_ai_call';
  const canUseTasksOrAICalls = company.package.can_use_tasks || company.package.can_use_ai_calls;

  return (
    <FormProvider {...form}>
      <form className={styles('recovery-step-form')} onSubmit={handleSubmit(submit)}>
        <div className={styles('head')}>{title}</div>
        <div className={styles('content')}>
          {type === 'task_generation' && !company.package.can_use_tasks && (
            <div>{t(i18nKeys.ACCOUNT_LIMITATION)}</div>
          )}

          {type === 'task_generation' && company.package.can_use_tasks && (
            <RecoveryStepFormCreateTask>
              <Input
                register={register('delay', { required: true, min: 1 })}
                errorMessage={errors.delay}
                label={t(i18nKeys.DELAY_WITH_PREVIOUS_ACTION)}
                type="number"
                withBorder
              />
            </RecoveryStepFormCreateTask>
          )}

          {type === 'send_reminder' && (
            <RecoveryStepFormSendReminder availableTemplateTypes={availableTemplateTypes}>
              <Input
                register={register('delay', { required: true, min: 1 })}
                errorMessage={errors.delay}
                label={t(i18nKeys.DELAY_WITH_PREVIOUS_ACTION)}
                type="number"
                withBorder
              />
            </RecoveryStepFormSendReminder>
          )}

          {type === 'initiate_ai_call' && company.package.can_use_ai_calls && (
            <RecoveryStepFormAICall />
          )}
        </div>
        {(isNotTaskOrAICall || canUseTasksOrAICalls) && (
          <div className={styles('button-wrapper')}>
            <Button type="submit" noMargin label={t(i18nKeys.SAVE)} disabled={!type} />
          </div>
        )}
      </form>
    </FormProvider>
  );
};
